import { useMemo } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { PrimaryHomepage, HomepageBodyText, Tooltip } from "@similarweb/ui-components";
import { Box, Stack, Text } from "@similarweb/ui-components/foundations";

import { swSettings } from "app/common/services/swSettings";
import { handleGainAccessRedirect } from "app/common/utils/handleGainAccessRedirect";
import { translate } from "app/filters/i18nFilter";
import ScrollToTopWrapper from "app/pages/sales-intelligence/common-components/ScrollToTopWrapper/ScrollToTopWrapper";
import { COMPANY_ANALYSIS_OVERVIEW, WEBSITE_ANALYSIS_PERFORMANCE } from "app/pages/sales-intelligence/constants/routes";
import createDefaultRecentCompanies from "app/pages/sales-intelligence/modules/company-analysis/helpers/defaultRecents";
import type { EnrichedRecent } from "app/pages/sales-intelligence/modules/company-analysis/hooks/useEnrichedRecents";
import useEnrichedRecents from "app/pages/sales-intelligence/modules/company-analysis/hooks/useEnrichedRecents";
import CompanyAutocomplete from "app/pages/sales-intelligence/modules/company-autocomplete/components/CompanyAutocomplete/CompanyAutocomplete";
import NoTouchContent from "app/pages/sales-intelligence/modules/quota/components/NotEnoughQuotaTooltip/NoTouchContent";
import TrialContent from "app/pages/sales-intelligence/modules/quota/components/NotEnoughQuotaTooltip/TrialContent";
import DataCreditsIndicatorContainer from "app/pages/sales-intelligence/modules/quota/containers/DataCreditsIndicatorContainer/DataCreditsIndicatorContainer";
import { StyledDCIndicatorHeaderWrapper } from "app/pages/sales-intelligence/modules/quota/containers/DataCreditsIndicatorContainer/styles";
import { useCompanyQuota } from "app/pages/sales-intelligence/modules/quota/hooks/useCompanyQuota";
import { useIsCompanyLockedByQuota } from "app/pages/sales-intelligence/modules/quota/hooks/useIsCompanyLockedByQuota";
import HomePageHeaderBackground from "app/pages/sales-intelligence/pages/home/components/HomePageHeaderBackground/HomePageHeaderBackground";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import homePageTrackingService from "app/pages/sales-intelligence/services/tracking/home-page/trackingService";
import { SearchType } from "app/pages/sales-intelligence/services/tracking/home-page/types";
import { isNoTouch, isTraditionalTrialUser } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import QuotaCard from "../QuotaCard/QuotaCard";
import RecentItem from "../RecentItem/RecentItem";

const GatePage = () => {
    const { enrichedRecents, isPending } = useEnrichedRecents();
    const isDataCreditsEnabled = salesSettingsHelper.isDataCreditsEnabled;
    const isNTUser = isNoTouch(swSettings);
    const isTrialUser = isTraditionalTrialUser(swSettings);
    const showNoTouchContent = isDataCreditsEnabled && (isNTUser || isTrialUser);
    const { recents, isRecentsEmpty } = useMemo(() => {
        if (enrichedRecents.length === 0) {
            return { recents: createDefaultRecentCompanies(), isRecentsEmpty: true };
        }
        return { recents: enrichedRecents, isRecentsEmpty: false };
    }, [enrichedRecents]);
    const { isQuotaUnlimited, data: quota } = useCompanyQuota();
    const isCompanyLocked = useIsCompanyLockedByQuota();

    const handleCompanyClick = ({ key, companyName }: { key: string; companyName: string }) => {
        homePageTrackingService.trackCompanyClicked(companyName);
        void proNavigator.go(COMPANY_ANALYSIS_OVERVIEW, {
            key,
        });
    };

    const handleWebsiteClick = (domain: string) => {
        homePageTrackingService.trackDomainClicked(SearchType.New, domain);
        void proNavigator.go(WEBSITE_ANALYSIS_PERFORMANCE, { key: domain, isWWW: "*", duration: "3m", country: 999 });
    };

    const onUpgradeClick = () => {
        handleGainAccessRedirect("Overviews hook", "click", "sales intelligence hooks", "Recents tooltip");
    };

    const tooltipContent = useMemo(() => {
        if (isDataCreditsEnabled && isTrialUser) {
            return <TrialContent />;
        }

        if (isDataCreditsEnabled && isNTUser) {
            return <NoTouchContent />;
        }

        return (
            <Stack>
                <Text
                    fontVariant="tooltip"
                    color={colorsPalettes.bluegrey["100"]}
                >
                    {translate(`si.pages.home.recents.quotaReached.tooltip.${quota?.interval}`)}
                </Text>
                <Text
                    fontVariant="tooltip"
                    color={colorsPalettes.blue["300"]}
                    onClick={onUpgradeClick}
                    cursor="pointer"
                >
                    {translate("si.common.upgrade_now")}
                </Text>
            </Stack>
        );
    }, [quota, isDataCreditsEnabled, isNTUser, isTrialUser, onUpgradeClick]);

    return (
        <ScrollToTopWrapper>
            <StyledDCIndicatorHeaderWrapper>
                <DataCreditsIndicatorContainer />
            </StyledDCIndicatorHeaderWrapper>
            <PrimaryHomepage
                subtitlePosition="left-aligned"
                headerBackgroundColor="transparent"
                searchComponents={
                    <CompanyAutocomplete
                        onCompanyClick={handleCompanyClick}
                        onWebsiteClick={handleWebsiteClick}
                    />
                }
                title={<Text fontVariant="h3">{translate("primary.sidebar.salesIntelligence.companyOverview")}</Text>}
                usecaseItems={
                    <>
                        {isQuotaUnlimited || showNoTouchContent ? null : <QuotaCard quota={quota} />}
                        {!isPending ? (
                            <>
                                <HomepageBodyText>
                                    {translate(
                                        isRecentsEmpty ? "si.company-analysis.suggestions" : "common.recent.searches",
                                    )}
                                </HomepageBodyText>
                                {recents.map((recentItem) => {
                                    const enrichedRecent = recentItem as EnrichedRecent;
                                    const isDisabledRecentItem = isCompanyLocked(
                                        enrichedRecent.companyData?.isPurchased,
                                    );
                                    return (
                                        <Tooltip
                                            content={tooltipContent}
                                            key={`${enrichedRecent.id}_${enrichedRecent.data.type}`}
                                            enabled={isDisabledRecentItem}
                                        >
                                            <Box>
                                                <RecentItem
                                                    recent={enrichedRecent}
                                                    isDisabled={isDisabledRecentItem}
                                                    isSuggestion={isRecentsEmpty}
                                                    isDataCreditsEnabled={isDataCreditsEnabled}
                                                />
                                            </Box>
                                        </Tooltip>
                                    );
                                })}
                            </>
                        ) : null}
                        <Box margin="0 0 68px" />
                    </>
                }
            />

            <HomePageHeaderBackground />
        </ScrollToTopWrapper>
    );
};

export default GatePage;
